import DOMPurify from "dompurify";
import { marked } from "marked";

/**
 * Rendu du markdown du depot (dossiers d'idees, guides des gestes), et reecriture de
 * leurs liens.
 *
 * Deux regles de la spec (sections 2 et 6) sont tenues ici, et nulle part ailleurs :
 *
 * - **Le markdown est nettoye avant d'etre pose dans la page.** `marked` produit du HTML
 *   depuis un fichier du depot ; DOMPurify le passe au tamis. L'ordre compte : on nettoie
 *   d'abord, on ajoute nos propres attributs ensuite, sinon un `rel` pose avant le tamis
 *   pourrait etre retire par lui.
 * - **Les liens relatifs sont reecrits.** Vers l'onglet quand la cible est un fichier du
 *   meme dossier, vers GitHub sinon. Un lien externe repart avec `rel="noopener"` : sans
 *   lui, la page ouverte garde une poignee sur celle-ci.
 *
 * `reecrireLien` est pure et testee seule : c'est la seule partie qui a des cas tordus
 * (`./`, `../`, ancre, requete, schema, chemin absolu).
 */

/**
 * Ce que le tamis retire en plus de ses refus par defaut.
 *
 * DOMPurify arrete les scripts et les gestionnaires d'evenements, mais laisse passer
 * `button`, `form`, `input` et l'attribut `style` : du HTML brut ecrit dans un fichier du
 * depot pourrait donc poser un faux bouton dans une page, ou cacher du texte dans un bloc
 * de code. Aucun fichier de `docs/` n'a besoin de ces balises, et la page d'un guide fait
 * copier des commandes : le tamis est resserre pour tout le markdown de la console.
 */
const TAMIS = {
  FORBID_TAGS: ["button", "form", "input", "select", "textarea", "style"],
  // Les quatre facons de cacher du texte sans script. `class` part avec les autres : la
  // console ne style rien par une classe venue d'un fichier (`language-*` compris, faute
  // de coloration), mais `hidden` ou `class="sr-only"` suffiraient a faire copier autre
  // chose que ce qui est a l'ecran.
  // `dir` reordonne ce qui s'affiche sans changer ce qui est copie : meme famille.
  FORBID_ATTR: ["style", "class", "hidden", "aria-hidden", "dir"],
};

/** Ou pointe un lien relatif qui sort du dossier de l'idee. */
export const GITHUB_BLOB = "https://github.com/kavaliero/ai-revenue-lab/blob/main/";

/** Racine des dossiers d'idees dans le depot. */
const RACINE_IDEES = "docs/idees";

/** Un lien qui porte un schema (`https:`, `mailto:`, `javascript:`) n'est pas relatif. */
const SCHEMA = /^[a-z][a-z0-9+.-]*:/i;

/**
 * Les seuls schemas rendus tels quels. DOMPurify retire deja un `href` en `javascript:`
 * avant la reecriture, mais `reecrireLien` est exportee et testee seule : elle ne doit pas
 * compter sur ce qui la precede. Tout autre schema (`javascript:`, `data:`, `vbscript:`)
 * repart sans cible.
 */
const SCHEMAS_ADMIS = /^(https?|mailto):/i;

export type ContexteLiens = {
  /**
   * Dossier du fichier rendu, relatif a la racine du depot, sans barre finale :
   * `docs/idees/cinepass` pour la fiche, `docs/idees/cinepass/gestes` pour le guide d'un
   * prerequis, `docs/gestes` pour un geste hors idee. C'est de la que partent les liens
   * relatifs : un guide vit un cran plus bas que la fiche, et resoudre depuis le dossier
   * de l'idee lui donnerait des liens faux.
   */
  readonly dossier: string;
  /** Noms des fichiers du dossier, tels que l'API les rend (`00-fiche.md`). */
  readonly fichiers: readonly string[];
};

/** Le dossier d'une idee dans le depot : `cinepass` donne `docs/idees/cinepass`. */
export function dossierIdee(slug: string): string {
  return `${RACINE_IDEES}/${slug}`;
}

export type LienReecrit = {
  readonly href: string;
  /** Nom du fichier a ouvrir quand le lien designe un onglet de la page. */
  readonly onglet: string | null;
  /** true quand le lien quitte la console : il part alors avec `rel="noopener"`. */
  readonly externe: boolean;
};

/** Enleve les `.` et les `..` d'un chemin, sans jamais remonter au-dessus de la racine. */
function normaliser(chemin: string): string {
  const morceaux: string[] = [];
  for (const morceau of chemin.split("/")) {
    if (morceau === "" || morceau === ".") continue;
    if (morceau === "..") {
      morceaux.pop();
      continue;
    }
    morceaux.push(morceau);
  }
  return morceaux.join("/");
}

/** `decodeURIComponent` leve sur un `%` isole : un lien tordu ne casse pas la page. */
function decoder(chemin: string): string {
  try {
    return decodeURIComponent(chemin);
  } catch {
    return chemin;
  }
}

/**
 * Ou mene un lien ecrit dans un fichier du dossier `contexte.dossier`.
 *
 * - ancre, lien vide : inchange ;
 * - lien avec schema : inchange s'il est `http`, `https` ou `mailto` (externe s'il part sur
 *   le web), vide pour tout autre schema ;
 * - chemin absolu (`/docs/...`) : GitHub, la barre en tete retiree ;
 * - chemin relatif qui designe un fichier du dossier : l'onglet correspondant ;
 * - tout le reste : GitHub, resolu depuis le dossier de l'idee.
 */
export function reecrireLien(href: string, contexte: ContexteLiens): LienReecrit {
  const brut = href.trim();
  if (brut === "" || brut.startsWith("#")) {
    return { href: brut, onglet: null, externe: false };
  }
  if (SCHEMA.test(brut)) {
    if (!SCHEMAS_ADMIS.test(brut)) {
      return { href: "", onglet: null, externe: false };
    }
    return { href: brut, onglet: null, externe: /^https?:/i.test(brut) };
  }

  // La requete et l'ancre suivent le chemin sans entrer dans sa resolution.
  const coupure = brut.search(/[?#]/);
  const chemin = coupure === -1 ? brut : brut.slice(0, coupure);
  const suffixe = coupure === -1 ? "" : brut.slice(coupure);

  const dossier = contexte.dossier;
  const resolu = brut.startsWith("/") ? normaliser(chemin) : normaliser(`${dossier}/${chemin}`);

  // Le nom du fichier est compare decode (`05%20spec.md` designe `05 spec.md`), mais
  // l'adresse GitHub garde le chemin tel qu'il est ecrit : c'est deja une URL valide.
  const decode = decoder(resolu);
  if (!brut.startsWith("/") && decode.startsWith(`${dossier}/`)) {
    const nom = decode.slice(dossier.length + 1);
    if (contexte.fichiers.includes(nom)) {
      return { href: `#${encodeURIComponent(nom)}`, onglet: nom, externe: false };
    }
  }
  return { href: `${GITHUB_BLOB}${resolu}${suffixe}`, onglet: null, externe: true };
}

/**
 * Reecrit les liens d'un fragment HTML deja nettoye. Separe du rendu pour rester
 * testable sans markdown, et parce que `renderMarkdown` doit nettoyer avant de poser
 * `rel` et `target`.
 */
export function reecrireLiensHtml(html: string, contexte: ContexteLiens): string {
  const page = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  reecrireLiensDom(page.body, contexte);
  return page.body.innerHTML;
}

/** Reecrit en place les ancres d'un arbre deja nettoye. */
function reecrireLiensDom(racine: ParentNode, contexte: ContexteLiens): void {
  for (const ancre of racine.querySelectorAll("a[href]")) {
    const { href, onglet, externe } = reecrireLien(ancre.getAttribute("href") ?? "", contexte);
    ancre.setAttribute("href", href);
    if (onglet !== null) {
      ancre.setAttribute("data-onglet", onglet);
    }
    if (externe) {
      // `noopener` est la demande de la spec ; `noreferrer` va avec et ne coute rien.
      ancre.setAttribute("rel", "noopener noreferrer");
      ancre.setAttribute("target", "_blank");
    }
  }
}

/**
 * Markdown d'un fichier du dossier, rendu, nettoye, liens reecrits.
 *
 * DOMPurify rend ici l'arbre nettoye plutot qu'une chaine : les ancres sont reecrites
 * dessus et le HTML n'est serialise qu'une fois, juste avant d'etre pose dans la page.
 * Chaque aller-retour chaine / arbre en moins est une surface de moins pour un mXSS.
 */
export function renderMarkdown(markdown: string, contexte: ContexteLiens): string {
  const html = marked.parse(markdown, { async: false, gfm: true });
  const fragment = DOMPurify.sanitize(html, {
    RETURN_DOM_FRAGMENT: true,
    ...TAMIS,
  });
  reecrireLiensDom(fragment, contexte);
  const conteneur = document.createElement("div");
  conteneur.append(fragment);
  return conteneur.innerHTML;
}
